import { cva, type VariantProps } from 'class-variance-authority';
import { InfoIcon, type LucideIcon, OctagonXIcon, TriangleAlertIcon } from 'lucide-react';

import { cn } from '@/utils/styles';

/** The severity scale, ordered most to least severe. */
export const SEVERITY_LEVELS = ['critical', 'warning', 'info'] as const;

export type SeverityLevel = (typeof SEVERITY_LEVELS)[number];

/** Text colour class per level, for styling a label or icon that isn't a {@link SeverityIcon}. */
export const severityAccent: Record<SeverityLevel, string> = {
  critical: 'text-destructive',
  warning: 'text-status-warning',
  info: 'text-status-info',
};

/** Background colour class per level, for a swatch or bar that isn't a {@link SeverityDot}. */
export const severityDot: Record<SeverityLevel, string> = {
  critical: 'bg-destructive',
  warning: 'bg-status-warning',
  info: 'bg-status-info',
};

const severityIcons: Record<SeverityLevel, LucideIcon> = {
  critical: OctagonXIcon,
  warning: TriangleAlertIcon,
  info: InfoIcon,
};

const severityIconVariants = cva('size-4 shrink-0', {
  variants: {
    tone: {
      accent: '',
      current: 'text-current',
    },
  },
  defaultVariants: {
    tone: 'accent',
  },
});

/** How a {@link SeverityIcon} is coloured. */
export type SeverityTone = NonNullable<VariantProps<typeof severityIconVariants>['tone']>;

export type SeverityIconProps = {
  /** Severity to render — selects the glyph, and the accent colour unless `tone` is `'current'`. */
  level: SeverityLevel;
  /**
   * `'accent'` colours the icon by severity; `'current'` inherits the surrounding text colour, for
   * hosts that do their own colouring (Toast does). Defaults to `'accent'`.
   */
  tone?: SeverityTone;
  /** Extra classes merged onto the icon, e.g. to override its `size-4` default. */
  className?: string;
};

/**
 * Icon for a level on the shared critical/warning/info scale, accented by level unless the host
 * colours it. The scale is independent of any API enum — map a domain enum onto {@link SeverityLevel}.
 * @param props - {@link SeverityIconProps}
 */
export function SeverityIcon({ level, tone = 'accent', className }: SeverityIconProps) {
  const Icon = severityIcons[level];
  return (
    <Icon
      data-slot="severity-icon"
      data-level={level}
      className={cn(severityIconVariants({ tone }), tone === 'accent' && severityAccent[level], className)}
    />
  );
}

export type SeverityDotProps = {
  /** Severity to render — selects the dot's colour. */
  level: SeverityLevel;
  /** Extra classes merged onto the dot, e.g. to override its `size-2` default. */
  className?: string;
};

/**
 * Dot in the level's colour, for dense rows where an icon reads too heavy. Decorative and always
 * `aria-hidden`, so keep the severity conveyed in adjacent text.
 * @param props - {@link SeverityDotProps}
 */
export function SeverityDot({ level, className }: SeverityDotProps) {
  return (
    <span
      aria-hidden
      data-slot="severity-dot"
      data-level={level}
      className={cn('size-2 shrink-0 rounded-full', severityDot[level], className)}
    />
  );
}
